import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Upload,
  Dropdown,
  Empty,
  Form as AntForm,
  Image,
  Input,
  List,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import {
  Button as BsButton,
  Card as BsCard,
  Form,
  Modal as BsModal,
} from "react-bootstrap";
import {
  ArrowLeftOutlined,
  CompressOutlined,
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  EyeOutlined,
  ExpandOutlined,
  IdcardOutlined,
  PlusOutlined,
  RightOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import { useUserManagement } from "hooks";
import useFormattedDate from "hooks/useFormatDate";
import Guard from "components/access-control/rbac/Guard";
import { PRIV } from "components/access-control/rbac/privileges";
import paths from "routes/paths";
import Avatar from "components/common/Avatar";
import CopyableText from "components/common/CopyableText";
import placeholderPhoto from "assets/img/profileplaceholder.png";
import olreadiaService from "services/olreadiaService";
import apiClient from "utils/apiClient";
import { STORAGE_BASE_URL } from "constants";
import { useAuth } from "contexts/AuthContext";
import { useFileUpload } from "hooks/useFileUpload";
import useAppConfig from "hooks/access-control/useAppConfig";
import GooglePlacesTagsSelect from "components/common/GooglePlacesTagsSelect";
import GooglePlacesAutocompleteInput from "components/common/GooglePlacesAutocompleteInput";
import {
  locationFieldsFromPlaceDetails,
  regionSummaryFromPlaceDetails,
} from "utils/googlePlaces";

const STATUS_OPTIONS = [
  { value: "NONE", label: "None" },
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "VERIFIED", label: "Verified" },
  { value: "SUSPENDED", label: "Suspended" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "TERMINATED", label: "Terminated" },
  { value: "DELETED", label: "Deleted" },
];

const STATUS_COLORS = {
  NONE: "default",
  PENDING: "gold",
  APPROVED: "green",
  VERIFIED: "green",
  SUSPENDED: "orange",
  CANCELLED: "red",
  TERMINATED: "red",
  DELETED: "red",
};

const toText = (value) => {
  if (value === null || value === undefined || value === "") {
    return "Not provided";
  }
  return String(value);
};

const HelperProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { formatDateTime } = useFormattedDate();
  const { getHelperProfileByUserId, updateHelperProfileStatus } =
    useUserManagement();
  const { user } = useAuth();
  const { uploadFile } = useFileUpload();
  const { appConfig } = useAppConfig();
  const googleMapsApiKey =
    appConfig?.googleMapsApiKey || appConfig?.googleMapsApiMatrixKey || "";

  const [professionProfilesLoading, setProfessionProfilesLoading] = useState(false);
  const [professionProfiles, setProfessionProfiles] = useState([]);
  const [professionDrawerOpen, setProfessionDrawerOpen] = useState(false);
  const [professionDrawerMode, setProfessionDrawerMode] = useState("view"); // view | edit | create
  const [professionDrawerLoading, setProfessionDrawerLoading] = useState(false);
  const [professionDrawerRecord, setProfessionDrawerRecord] = useState(null);
  const [professionSaving, setProfessionSaving] = useState(false);
  const [professionDrawerFullWidth, setProfessionDrawerFullWidth] = useState(false);
  const [professionDrawerLockedProfileType, setProfessionDrawerLockedProfileType] =
    useState(null);
  const [professionAttachmentPreviewOpen, setProfessionAttachmentPreviewOpen] =
    useState(false);
  const [professionAttachmentPreviewUrl, setProfessionAttachmentPreviewUrl] =
    useState("");
  const [professionAttachmentPreviewTitle, setProfessionAttachmentPreviewTitle] =
    useState("");
  const [signedUrlByFileId, setSignedUrlByFileId] = useState({});
  const [professionDocumentsFiles, setProfessionDocumentsFiles] = useState([]);
  const [professionDocumentsUploading, setProfessionDocumentsUploading] = useState(false);
  const [professionServiceOfferingsLoading, setProfessionServiceOfferingsLoading] = useState(false);
  const [professionServiceOfferingOptions, setProfessionServiceOfferingOptions] = useState([]);
  const [professionForm] = AntForm.useForm();
  const professionEditScrollRef = useRef(null);
  const professionProfilesSectionRef = useRef(null);
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [helperProfile, setHelperProfile] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("NONE");
  const [notes, setNotes] = useState("");
  const [statusModalOpen, setStatusModalOpen] = useState(false);

  const loadHelperProfile = useCallback(async ({ silent = false } = {}) => {
    if (!userId) return;
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      // eslint-disable-next-line no-console
      console.log("[helper-profile] fetch helper profile", { userId });
      const profile = await getHelperProfileByUserId(userId);
      // eslint-disable-next-line no-console
      console.log("[helper-profile] fetch helper profile response", profile);
      setHelperProfile(profile);
      setSelectedStatus(profile?.helperStatus || profile?.status || "NONE");
      setNotes(profile?.statusNotes || profile?.notes || "");
    } catch (err) {
      if (!silent) {
        setError(err?.message || "Failed to load helper profile");
        setHelperProfile(null);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [getHelperProfileByUserId, userId]);

  useEffect(() => {
    loadHelperProfile();
  }, [loadHelperProfile]);

  const loadProfessionServiceOfferings = useCallback(async () => {
    setProfessionServiceOfferingsLoading(true);
    try {
      const res = await olreadiaService.serviceOfferings.list({ page: 0, size: 200 });
      const content = Array.isArray(res?.content) ? res.content : Array.isArray(res) ? res : [];
      const options = content
        .map((s) => {
          const code = s?.code || s?.briefName || s?.name || s?.id;
          if (!code) return null;
          const label = s?.name ? `${s.name} (${code})` : String(code);
          return { label, value: String(code) };
        })
        .filter(Boolean);
      setProfessionServiceOfferingOptions(options);
    } catch (err) {
      console.error("[helper-professions] service offerings fetch failed", err);
      setProfessionServiceOfferingOptions([]);
    } finally {
      setProfessionServiceOfferingsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfessionServiceOfferings();
  }, [loadProfessionServiceOfferings]);

  const loadProfessionProfiles = useCallback(async () => {
    // Profession profiles in olreadia-service are keyed by the user's UUID (not a separate helper-profile id).
    const helperId = userId;
    if (!helperId) return;
    setProfessionProfilesLoading(true);
    try {
      // eslint-disable-next-line no-console
      console.log("[helper-professions] listByUser request", { userId: helperId });
      const res = await olreadiaService.professionProfiles.listByUser(helperId);
      console.log("[helper-professions] listForHelper response", res);
      const nextProfiles = Array.isArray(res)
        ? res
        : Array.isArray(res?.content)
          ? res.content
          : [];
      setProfessionProfiles(nextProfiles);
    } catch (err) {
      console.error("[helper-professions] listForHelper failed", err);
      setProfessionProfiles([]);
    } finally {
      setProfessionProfilesLoading(false);
    }
  }, [helperProfile?.id, userId]);

  useEffect(() => {
    if (!helperProfile?.id && !userId) return;
    loadProfessionProfiles();
  }, [helperProfile?.id, userId, loadProfessionProfiles]);

  useEffect(() => {
    if (location.hash !== "#profession-profiles" && location.hash !== "#helper-profession-profiles") return;
    professionProfilesSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.hash]);

  const currentStatus = helperProfile?.helperStatus || helperProfile?.status || "NONE";
  const currentNotes = helperProfile?.statusNotes || helperProfile?.notes || "";
  const statusChanged = selectedStatus !== currentStatus;
  const notesChanged = notes.trim() !== currentNotes.trim();

  const openStatusModal = useCallback(() => {
    setSelectedStatus(currentStatus);
    setNotes(currentNotes);
    setStatusModalOpen(true);
  }, [currentNotes, currentStatus]);

  const helperSkills = useMemo(() => {
    if (!Array.isArray(helperProfile?.helperSkills)) return [];

    return helperProfile.helperSkills
      .map((entry, index) => {
        if (!entry) return null;

        if (typeof entry === "string") {
          return {
            key: `skill-${index}`,
            name: entry,
          };
        }

        const nestedSkill = entry?.skill && typeof entry.skill === "object"
          ? entry.skill
          : null;
        const name = nestedSkill?.name || entry?.name || "";
        if (!name) return null;

        return {
          key: entry.id || nestedSkill?.id || `skill-${index}`,
          name,
          description: nestedSkill?.description || entry?.description || "",
          proficiencyLevel: entry?.proficiencyLevel || null,
          yearsOfExperience:
            typeof entry?.yearsOfExperience === "number"
              ? entry.yearsOfExperience
              : null,
          endorsed: entry?.endorsed === true,
          upVotes: entry?.upVotes === true,
        };
      })
      .filter(Boolean);
  }, [helperProfile]);

  const helperDetails = useMemo(
    () => [
      {
        label: "ID",
        value: <CopyableText value={helperProfile?.id} />,
      },
      {
        label: "Helper Status",
        value: (
          <Space size={8} align="center">
            <Tag color={STATUS_COLORS[currentStatus] || "default"}>
              {currentStatus}
            </Tag>
            <Guard privilege={PRIV.UPDATE_USERS}>
              <BsButton
                type="button"
                size="sm"
                variant="outline-success"
                className="text-xs"
                onClick={openStatusModal}
                aria-label="Edit helper status"
                title="Edit helper status"
              >
                <FontAwesomeIcon icon="edit" />
              </BsButton>
            </Guard>
          </Space>
        ),
      },
      {
        label: "Status Notes",
        value: toText(helperProfile?.statusNotes || helperProfile?.notes),
      },
      {
        label: "Preferred First Name",
        value: toText(helperProfile?.preferredFirstName),
      },
      {
        label: "Preferred Last Name",
        value: toText(helperProfile?.preferredLastName),
      },
      { label: "First Name", value: toText(helperProfile?.firstName) },
      { label: "Last Name", value: toText(helperProfile?.lastName) },
      { label: "Email", value: toText(helperProfile?.email) },
      { label: "Phone", value: toText(helperProfile?.phone) },
      { label: "Gender", value: toText(helperProfile?.gender) },
      { label: "Location", value: toText(helperProfile?.location) },
      { label: "Completed Tasks", value: helperProfile?.completedTasks ?? 0 },
      { label: "Average Rating", value: helperProfile?.averageRating ?? 0 },
      {
        label: "Signed Up At",
        value: formatDateTime(helperProfile?.signedUpAt),
      },
      {
        label: "Verified",
        value: helperProfile?.verified ? (
          <Tag color="green">Yes</Tag>
        ) : (
          <Tag>No</Tag>
        ),
      },
      
    ],
    [currentStatus, formatDateTime, helperProfile, openStatusModal],
  );

  const helperDisplayName = useMemo(() => {
    const firstName = helperProfile?.firstName || "";
    const lastName = helperProfile?.lastName || "";
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || "this helper";
  }, [helperProfile?.firstName, helperProfile?.lastName]);

  const generalProfessionProfiles = useMemo(
    () =>
      (professionProfiles || []).filter(
        (p) => String(p?.profileType || "").toUpperCase() === "GENERAL",
      ),
    [professionProfiles],
  );

  const specializedProfessionProfiles = useMemo(
    () =>
      (professionProfiles || []).filter(
        (p) => String(p?.profileType || "").toUpperCase() === "SPECIALIZED",
      ),
    [professionProfiles],
  );

  const closeProfessionDrawer = useCallback(() => {
    setProfessionDrawerOpen(false);
    setProfessionDrawerRecord(null);
    setProfessionDrawerMode("view");
    setProfessionDrawerLoading(false);
    setProfessionSaving(false);
    setProfessionDrawerFullWidth(false);
    setProfessionDrawerLockedProfileType(null);
    setProfessionDocumentsFiles([]);
    setProfessionDocumentsUploading(false);
    professionForm.resetFields();
  }, [professionForm]);

  const ensureUploaderId = () => user?.id;

  const syncDocumentsFormValueFromFileList = (fileList) => {
    const items = (Array.isArray(fileList) ? fileList : [])
      .map((f) => {
        const fileId = extractFileId(f?.fileId || f?.uid || f?.response?.fileId);
        if (!fileId) return null;
        const caption = String(f?.caption || f?.name || "").trim() || null;
        // Backend expects HelperDocumentDTO { id, caption }
        return { id: String(fileId), ...(caption ? { caption } : {}) };
      })
      .filter(Boolean);
    professionForm.setFieldValue("documents", items);
  };

  const resolveFileUrl = (value) => {
    if (!value) return null;
    const str = String(value);
    if (str.includes("://")) return str;
    return `${STORAGE_BASE_URL}/files/${str}`;
  };

  const extractFileId = (value) => {
    if (!value) return null;
    if (typeof value === "string") return value.trim() || null;
    if (typeof value === "number") return String(value);
    if (typeof value === "object") {
      return (
        value.fileId ||
        value.fid ||
        value.id ||
        value.file_id ||
        value.file?.fileId ||
        value.file?.id ||
        null
      );
    }
    return null;
  };

  const isImageUrl = (url) =>
    /\.(png|jpe?g|webp|gif|bmp|svg)(\?|$)/i.test(String(url || ""));

  const isPdfUrl = (url) => /\.pdf(\?|$)/i.test(String(url || ""));

  const fetchSignedUrlForFileId = async (fileId) => {
    const resolvedFileId = extractFileId(fileId);
    if (!resolvedFileId) return null;
    const cached = signedUrlByFileId?.[String(resolvedFileId)];
    if (cached) return cached;

    const endpoint = `/storage-service/api/v1/files/photo/signed-urls?fids=${encodeURIComponent(
      String(resolvedFileId),
    )}`;

    try {
      console.log("[helper-professions] signed url request", {
        fileId: resolvedFileId,
        rawFileId: fileId,
        endpoint,
      });
      const response = await apiClient.get(endpoint);
      console.log("[helper-professions] signed url response", {
        fileId: resolvedFileId,
        data: response?.data,
      });
      const data = response?.data;
      const signedUrl =
        (typeof data === "string" && data.trim()) ||
        (Array.isArray(data) && data[0]) ||
        (Array.isArray(data?.signedUrls) && data.signedUrls[0]) ||
        (typeof data?.url === "string" && data.url) ||
        null;
      if (signedUrl) {
        setSignedUrlByFileId((prev) => ({
          ...prev,
          [String(resolvedFileId)]: signedUrl,
        }));
      }
      return signedUrl;
    } catch (err) {
      console.error("[helper-professions] signed url fetch failed", {
        fileId: resolvedFileId,
        rawFileId: fileId,
        endpoint,
        err,
      });
      return null;
    }
  };

  const openAttachmentPreview = async (fileId, label = "Attachment") => {
    const resolvedFileId = extractFileId(fileId);
    if (!resolvedFileId) return;
    let url = resolveFileUrl(resolvedFileId);
    const signed = await fetchSignedUrlForFileId(resolvedFileId);
    if (signed) url = signed;

    if (!url) {
      message.warning("No preview available");
      return;
    }

    if (isPdfUrl(url) || (!isImageUrl(url) && url.includes("://"))) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }

    setProfessionAttachmentPreviewTitle(label);
    setProfessionAttachmentPreviewUrl(url);
    setProfessionAttachmentPreviewOpen(true);
  };

  const renderAttachmentValue = (fileId, label = "Attachment") => {
    const resolvedFileId = extractFileId(fileId);
    if (!resolvedFileId) return "-";
    const cached = signedUrlByFileId?.[String(resolvedFileId)] || null;
    const url = cached || resolveFileUrl(resolvedFileId);
    if (!url) return "-";

    if (isImageUrl(url)) {
      return (
        <button
          type="button"
          onClick={() => openAttachmentPreview(resolvedFileId, label)}
          style={{
            background: "transparent",
            border: 0,
            padding: 0,
            cursor: "pointer",
          }}
        >
          <img
            src={url}
            alt={label}
            style={{
              width: 72,
              height: 72,
              objectFit: "cover",
              borderRadius: 8,
              border: "1px solid #f0f0f0",
            }}
          />
        </button>
      );
    }

    return (
      <Tag
        color="default"
        style={{ cursor: "pointer", userSelect: "none" }}
        onClick={() => openAttachmentPreview(resolvedFileId, label)}
      >
        {isPdfUrl(url) ? "PDF" : "FILE"} • {String(resolvedFileId).slice(0, 8)}
      </Tag>
    );
  };

  useEffect(() => {
    if (!professionDrawerOpen) return;
    if (professionDrawerMode !== "view") return;
    const record = professionDrawerRecord;
    if (!record) return;

    const docIdsRaw = Array.isArray(record?.documents) ? record.documents : [];
    const certIds = Array.isArray(record?.certifications)
      ? record.certifications.map((c) => c?.fileId || c?.file?.id).filter(Boolean)
      : [];

    const ids = [
      ...new Set(
        [...docIdsRaw, ...certIds]
          .map((v) => extractFileId(v))
          .filter(Boolean)
          .map((v) => String(v)),
      ),
    ];
    if (!ids.length) return;

    // best-effort prefetch so thumbnails render; signed URLs may expire.
    Promise.allSettled(ids.map((id) => fetchSignedUrlForFileId(id))).catch(() => null);
  }, [professionDrawerMode, professionDrawerOpen, professionDrawerRecord]);

  useEffect(() => {
    if (!professionDrawerOpen) return;
    if (professionDrawerMode === "view") return;
    const ids = (Array.isArray(professionDocumentsFiles) ? professionDocumentsFiles : [])
      .map((f) => extractFileId(f?.fileId || f?.uid))
      .filter(Boolean);
    if (!ids.length) return;
    Promise.allSettled(ids.map((id) => fetchSignedUrlForFileId(id))).catch(() => null);
  }, [professionDrawerMode, professionDrawerOpen, professionDocumentsFiles]);

  const openProfessionDrawer = useCallback(
    async ({ mode, record, presetType } = {}) => {
      const helperId = helperProfile?.id || userId;
      if (!helperId) return;

      setProfessionDrawerOpen(true);
      setProfessionDrawerMode(mode || "view");
      setProfessionDrawerRecord(record || null);

      if (mode === "create") {
        setProfessionDrawerLockedProfileType(presetType || null);
        setProfessionDocumentsFiles([]);
        professionForm.setFieldsValue({
          id: undefined,
          helperId,
          profileType: presetType || "GENERAL",
          sectorCode: "",
          profileName: "",
          profileSummary: "",
          description: "",
          experience: "",
          educationLevel: undefined,
          previousRegionsWorkedIn: [],
          documents: [],
          certifications: [],
          references: [],
          education: [],
          workExperienceHistory: [],
          interestedServices: [],
          interestedLocations: [],
          interestedShifts: [],
        });
        return;
      }

      // IMPORTANT: Do not call GET /helper-professions/{id} here.
      // Some records in the DB may have `documents` stored as objects instead of UUID strings,
      // causing the backend to respond with 400 while trying to deserialize List<UUID>.
      // The list endpoint already returns enough data for the admin view/edit UI.
      if (!record?.id) return;
      const safe = record || {};
      const docs = Array.isArray(safe?.documents) ? safe.documents : [];
      setProfessionDocumentsFiles(
        docs
          .map((d, idx) => {
            const fid = extractFileId(d?.fileId || d?.id || d);
            if (!fid) return null;
            const caption = typeof d === "object" ? d?.caption || d?.name || null : null;
            return {
              uid: String(fid),
              fileId: String(fid),
              name: caption ? String(caption) : `document-${idx + 1}`,
              caption: caption ? String(caption) : undefined,
              status: "done",
            };
          })
          .filter(Boolean),
      );
      professionForm.setFieldsValue({
        id: safe?.id,
        helperId: safe?.helperId || helperId,
        profileType: safe?.profileType,
        sectorCode: safe?.sector?.code || safe?.sectorCode || "",
        profileName: safe?.profileName || "",
        profileSummary: safe?.profileSummary || "",
        description: safe?.description || "",
        experience: safe?.experience || "",
        educationLevel: safe?.educationLevel || undefined,
        previousRegionsWorkedIn: Array.isArray(safe?.previousRegionsWorkedIn)
          ? safe.previousRegionsWorkedIn
          : [],
        documents: docs
          .map((d) => {
            const fid = extractFileId(d?.fileId || d?.id || d);
            if (!fid) return null;
            if (typeof d === "object") {
              const caption = String(d?.caption || d?.name || "").trim() || null;
              return { id: String(fid), ...(caption ? { caption } : {}) };
            }
            return { id: String(fid) };
          })
          .filter(Boolean),
        certifications: Array.isArray(safe?.certifications) ? safe.certifications : [],
        references: Array.isArray(safe?.references) ? safe.references : [],
        education: Array.isArray(safe?.education) ? safe.education : [],
        workExperienceHistory: Array.isArray(safe?.workExperienceHistory) ? safe.workExperienceHistory : [],
        interestedServices: Array.isArray(safe?.interestedServices)
          ? safe.interestedServices
              .map((s) => s?.code || s?.name || s?.id)
              .filter(Boolean)
          : [],
        interestedLocations: Array.isArray(safe?.interestedLocations)
          ? safe.interestedLocations
          : [],
        interestedShifts: Array.isArray(safe?.interestedShifts) ? safe.interestedShifts : [],
      });
    },
    [helperProfile?.id, professionForm, userId],
  );

  const saveProfessionProfile = useCallback(
    async (values) => {
      const helperId = helperProfile?.id || userId;
      if (!helperId) return;

      const normalizeInterestedServices = (raw) => {
        if (!Array.isArray(raw)) return [];
        return raw
          .map((v) => {
            if (!v) return null;
            if (typeof v === "string") return { code: v.trim() };
            if (typeof v === "object") {
              const code = v.code || v.id || v.name;
              return code ? { ...v, code: String(code).trim() } : null;
            }
            return null;
          })
          .filter(Boolean)
          .filter((v) => v.code);
      };

      const normalizeInterestedLocations = (raw) => {
        if (!Array.isArray(raw)) return [];
        return raw
          .map((v) => {
            if (!v || typeof v !== "object") return null;
            return {
              city: v.city || "",
              state: v.state || "",
              county: v.county || "",
              country: v.country || "",
              open: Boolean(v.open),
            };
          })
          .filter(Boolean);
      };

      const payload = {
        id: values?.id || undefined,
        sectorCode: values?.sectorCode?.trim() || undefined,
        profileType: values?.profileType || undefined,
        profileName: values?.profileName?.trim() || undefined,
        profileSummary: values?.profileSummary?.trim() || undefined,
        description: values?.description?.trim() || undefined,
        experience: values?.experience?.trim() || undefined,
        // Keep key present so backend doesn't accidentally clear it when omitted.
        educationLevel: values?.educationLevel ?? null,
        previousRegionsWorkedIn: Array.isArray(values?.previousRegionsWorkedIn)
          ? values.previousRegionsWorkedIn.map((v) => String(v || "").trim()).filter(Boolean)
          : [],
        documents: Array.isArray(values?.documents) ? values.documents : [],
        certifications: Array.isArray(values?.certifications) ? values.certifications : [],
        references: Array.isArray(values?.references) ? values.references : [],
        education: Array.isArray(values?.education) ? values.education : [],
        workExperienceHistory: Array.isArray(values?.workExperienceHistory)
          ? values.workExperienceHistory
          : [],
        interestedServices: normalizeInterestedServices(values?.interestedServices),
        interestedLocations: normalizeInterestedLocations(values?.interestedLocations),
        interestedShifts: Array.isArray(values?.interestedShifts) ? values.interestedShifts : [],
      };

      if (!payload.profileType) {
        message.warning("Profile type is required.");
        return;
      }
      if (!payload.sectorCode) {
        message.warning("Sector code is required.");
        return;
      }

      if (professionSaving) return;
      setProfessionSaving(true);
      try {
        console.log("[helper-professions] save payload", {
          helperId,
          mode: professionDrawerMode,
          payload,
        });
        if (professionDrawerMode === "create") {
          const created = await olreadiaService.professionProfiles.createForUser(helperId, payload);
          console.log("[helper-professions] create response", created);
          message.success("Profession profile created.");
        } else {
          const updated = await olreadiaService.professionProfiles.updateForUser(helperId, payload);
          console.log("[helper-professions] update response", updated);
          message.success("Profession profile updated.");
        }
        await loadProfessionProfiles();
        closeProfessionDrawer();
      } catch (err) {
        console.error("[helper-professions] save failed", err);
        message.error(err?.message || "Failed to save profession profile");
      } finally {
        setProfessionSaving(false);
      }
    },
    [
      closeProfessionDrawer,
      helperProfile?.id,
      loadProfessionProfiles,
      professionDrawerMode,
      userId,
    ],
  );

  const handleDeleteProfessionProfile = (record) => {
    if (!record?.id) return;
    const resolveDeleteError = (err) => {
      const status = err?.response?.status;
      const backendMessage = String(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "",
      ).trim();

      if (
        status === 409 ||
        status === 400 ||
        /background check/i.test(backendMessage) ||
        /cannot be deleted/i.test(backendMessage) ||
        /delete.*background/i.test(backendMessage)
      ) {
        return "This profession profile cannot be deleted because it has background checks.";
      }

      return backendMessage || "Failed to delete profession profile";
    };

    Modal.confirm({
      title: "Delete profession profile?",
      content: (
        <Typography.Text type="secondary">
          This will permanently delete this profession profile for this user.
        </Typography.Text>
      ),
      okText: "Delete",
      cancelText: "Cancel",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const targetUserId = userId;
          await olreadiaService.professionProfiles.delete(record.id, { userId: targetUserId || undefined });
          message.success("Profession profile deleted.");
          await loadProfessionProfiles();
        } catch (err) {
          console.error("[helper-professions] delete failed", err);
          message.error(resolveDeleteError(err));
        }
      },
    });
  };

  const labelForProfile = (r) =>
    r?.profileName || r?.sector?.label || r?.sector?.code || "Profession Profile";

  const subtitleForProfile = (r) => r?.sector?.label || r?.sector?.code || "-";

  const getDocsCount = (r) => (Array.isArray(r?.documents) ? r.documents.length : 0);

  const getCertsCount = (r) =>
    Array.isArray(r?.certifications) ? r.certifications.length : 0;

  const ProfessionBucketCard = ({
    title,
    icon,
    description,
    count,
    addDisabled,
    onAdd,
    children,
  }) => (
    <Card
      size="small"
      style={{ borderRadius: 12, height: "100%" }}
      styles={{ body: { padding: 12 } }}
      title={
        <Space size={10}>
          <span
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#f5f5f5",
            }}
          >
            {icon}
          </span>
          <div style={{ lineHeight: 1.1 }}>
            <Typography.Text strong>{title}</Typography.Text>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {description}
              </Typography.Text>
            </div>
          </div>
          <Tag color="default" className="mb-0">
            {count}
          </Tag>
        </Space>
      }
      extra={
        <Button
          size="small"
          icon={<PlusOutlined />}
          onClick={(e) => {
            e?.stopPropagation?.();
            onAdd?.();
          }}
          disabled={addDisabled}
        >
          Add
        </Button>
      }
    >
      {children}
    </Card>
  );

  const renderProfessionCardList = (list = [], emptyText) => (
    <List
      loading={professionProfilesLoading}
      dataSource={list}
      locale={{ emptyText: emptyText || "No profiles yet." }}
      renderItem={(r) => {
        // Profession profiles in olreadia-service are keyed by the user's UUID.
        const helperId = userId;
        const title = labelForProfile(r);
        const subtitle = subtitleForProfile(r);
        const docsCount = getDocsCount(r);
        const certsCount = getCertsCount(r);
        const type = String(r?.profileType || "").toUpperCase();
        const menu = {
          items: [
            {
              key: "view",
              label: "View",
              onClick: () => {
                if (!r?.id) return;
                // eslint-disable-next-line no-console
                console.log("[HelperProfilePage] navigate profile details", {
                  targetProfileId: r.id,
                  payload: r,
                });
                navigate(paths.helperProfessionProfileDetails(r.id), {
                  state: {
                    profile: r,
                    helperProfile,
                    display: {
                      name:
                        helperProfile?.fullName ||
                        [helperProfile?.firstName, helperProfile?.middleName, helperProfile?.lastName]
                          .filter(Boolean)
                          .join(" ") ||
                        helperProfile?.userFullName ||
                        r?.profileName ||
                        "-",
                      email: helperProfile?.email || helperProfile?.userEmail || r?.userEmail || r?.email || "-",
                      phone: helperProfile?.phone || helperProfile?.userPhone || r?.userPhone || r?.phone || "-",
                      photo: helperProfile?.profilePhoto || helperProfile?.photo || r?.profilePhoto || r?.photo || r?.avatar || null,
                    },
                  },
                });
              },
            },
            {
              key: "edit",
              label: "Edit",
              onClick: () => {
                if (!helperId || !r?.id) return;
                navigate(
                  paths.helperProfessionProfileEditForHelper(helperId, r.id),
                  { state: { profile: r } },
                );
              },
            },
            {
              type: "divider",
            },
            {
              key: "delete",
              label: "Delete",
              danger: true,
              disabled: !r?.id,
              onClick: () => handleDeleteProfessionProfile(r),
            },
          ],
        };

        return (
          <List.Item
            style={{ paddingInline: 0, paddingBlock: 10 }}
            actions={[
              <Dropdown key="actions" trigger={["click"]} menu={menu}>
                <Button
                  type="text"
                  icon={<EllipsisOutlined />}
                  onClick={(e) => e?.stopPropagation?.()}
                  aria-label="Actions"
                />
              </Dropdown>,
            ]}
          >
            <List.Item.Meta
              title={
                <div
                  style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}
                  onClick={() => {
                    if (!r?.id) return;
                    // eslint-disable-next-line no-console
                    console.log("[HelperProfilePage] open profile card", {
                      targetProfileId: r.id,
                      payload: r,
                    });
                    navigate(paths.helperProfessionProfileDetails(r.id), {
                      state: {
                        profile: r,
                        helperProfile,
                        display: {
                          name:
                            helperProfile?.fullName ||
                            [helperProfile?.firstName, helperProfile?.middleName, helperProfile?.lastName]
                              .filter(Boolean)
                              .join(" ") ||
                            helperProfile?.userFullName ||
                            r?.profileName ||
                            "-",
                          email: helperProfile?.email || helperProfile?.userEmail || r?.userEmail || r?.email || "-",
                          phone: helperProfile?.phone || helperProfile?.userPhone || r?.userPhone || r?.phone || "-",
                          photo: helperProfile?.profilePhoto || helperProfile?.photo || r?.profilePhoto || r?.photo || r?.avatar || null,
                        },
                      },
                    });
                  }}
                >
                  <span
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 10,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#f5f5f5",
                      flexShrink: 0,
                    }}
                  >
                    {type === "GENERAL" ? <IdcardOutlined /> : <ToolOutlined />}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <Typography.Text strong style={{ display: "block" }} ellipsis>
                      {title}
                    </Typography.Text>
                    <Space size={6} wrap>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {subtitle}
                      </Typography.Text>
                      {type ? <Tag className="mb-0">{type}</Tag> : null}
                      <Tag color="default" className="mb-0">
                        {docsCount} docs
                      </Tag>
                      <Tag color="default" className="mb-0">
                        {certsCount} certs
                      </Tag>
                    </Space>
                  </div>
                </div>
              }
              description={null}
            />
          </List.Item>
        );
      }}
    />
  );

  const handleSaveStatus = async () => {
    if (!userId || !selectedStatus) return;
    try {
      setSaving(true);
      await updateHelperProfileStatus(userId, selectedStatus, notes || null);
      toast.success("Helper profile status updated");
      setStatusModalOpen(false);
      await loadHelperProfile({ silent: true });
    } catch (err) {
      toast.error(err?.message || "Failed to update helper profile status");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center"
      style={{ minHeight: "60vh" }}>
        <Spin size="default" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="mt-3">
        <Alert type="error" showIcon message="Failed to load helper profile" description={error} />
        <Button
          icon={<ArrowLeftOutlined />}
          className="mt-3"
          onClick={() => navigate(paths.userDetails(userId))}
        >
          Back to User Details
        </Button>
      </Card>
    );
  }

  if (!helperProfile) {
    return (
      <Card className="mt-3">
        <Empty description="No helper profile found for this user." />
        <div className="mt-3 text-center">
          <Button onClick={() => navigate(paths.userDetails(userId))}>
            Back to User Details
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <BsCard className="mb-4 shadow-none mt-3 rounded border-1">
        <BsCard.Body className="d-flex flex-wrap align-items-center justify-content-between py-3 px-4">
          <div className="d-flex align-items-center mb-2 mb-sm-0">
            <BsButton
              type="button"
              onClick={() => navigate(-1)}
              size="sm"
              aria-label="Go back"
              variant="outline-success"
            >
              <span style={{ fontSize: "1.3em", lineHeight: 1 }}>←</span> Back
            </BsButton>
          </div>

          <h4 className="mb-2 mb-sm-0 fw-semibold text-base flex-grow-1 text-center ms-3">
            Helper Profile
          </h4>

          <div
            className="d-flex gap-2 flex-wrap justify-content-center justify-content-sm-end"
            style={{ minWidth: 180 }}
          >
            <Guard privilege={PRIV.VIEW_USERS}>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => navigate(paths.userDetails(userId))}
                className="text-nowrap text-xs"
              >
                View User Profile
              </Button>
            </Guard>
          </div>
        </BsCard.Body>
      </BsCard>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <Card title="Photos">
              <Row gutter={[12, 12]}>
                <Col xs={24} md={12}>
                  <Typography.Text
                    type="secondary"
                    className="text-xxs d-block mb-2 text-center"
                  >
                    Security Photo
                  </Typography.Text>
                  <div className="d-flex justify-content-center">
                    <Avatar
                      src={helperProfile.securityPhoto || placeholderPhoto}
                      size="5xl"
                    />
                  </div>
                </Col>
                <Col xs={24} md={12}>
                  <Typography.Text
                    type="secondary"
                    className="text-xxs d-block mb-2 text-center"
                  >
                    Profile Photo
                  </Typography.Text>
                  <div className="d-flex justify-content-center">
                    <Avatar
                      src={helperProfile.profilePhoto || placeholderPhoto}
                      size="5xl"
                    />
                  </div>
                </Col>
              </Row>
            </Card>

            <Card title="About Helper">
              <Typography.Text type="secondary" className="d-block">
                Description
              </Typography.Text>
              <Typography.Paragraph className="mb-3">
                {toText(helperProfile.description)}
              </Typography.Paragraph>

              <Typography.Text strong>Helper Skills</Typography.Text>
              <div className="mt-2">
                {helperSkills.length ? (
                  <Space direction="vertical" size={8} style={{ width: "100%" }}>
                    {helperSkills.map((skill) => (
                      <div
                        key={skill.key}
                        className="border rounded p-2"
                        style={{ background: "#fafafa" }}
                      >
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                          <Tag color="blue" className="mb-0">
                            {skill.name}
                          </Tag>
                          {skill.proficiencyLevel && (
                            <Tag color="geekblue" className="mb-0">
                              {skill.proficiencyLevel}
                            </Tag>
                          )}
                          {typeof skill.yearsOfExperience === "number" && (
                            <Tag color="purple" className="mb-0">
                              {`${skill.yearsOfExperience} yrs`}
                            </Tag>
                          )}
                          {skill.endorsed && (
                            <Tag color="green" className="mb-0">
                              Endorsed
                            </Tag>
                          )}
                          {skill.upVotes && (
                            <Tag color="cyan" className="mb-0">
                              Upvoted
                            </Tag>
                          )}
                        </div>
                        {skill.description && (
                          <Typography.Text type="secondary" className="text-xxs d-block">
                            {skill.description}
                          </Typography.Text>
                        )}
                      </div>
                    ))}
                  </Space>
                ) : (
                  <Typography.Text type="secondary">No helper skills.</Typography.Text>
                )}
              </div>
            </Card>

            <div ref={professionProfilesSectionRef} id="profession-profiles">
            <Card
              title={
                <Space size={8}>
                  <IdcardOutlined />
                  <span>Helper Professions</span>
                </Space>
              }
              extra={
                <Button size="small" onClick={loadProfessionProfiles} loading={professionProfilesLoading}>
                  Refresh
                </Button>
              }
              >
                <Row gutter={[16, 16]}>
                <Col xs={24} md={24} xl={12}>
                  <ProfessionBucketCard
                    title="General"
                    icon={<IdcardOutlined />}
                    description="One main professional profile"
                    count={generalProfessionProfiles.length}
                    addDisabled={generalProfessionProfiles.length > 0}
                    onAdd={() => {
                      const helperId = helperProfile?.id || userId;
                      if (!helperId) return;
                      navigate(
                        `${paths.helperProfessionProfileCreateForHelper(helperId)}?type=GENERAL`,
                      );
                    }}
                  >
                    {renderProfessionCardList(generalProfessionProfiles, "No general profile.")}
                  </ProfessionBucketCard>
                </Col>

                <Col xs={24} md={24} xl={12}>
                  <ProfessionBucketCard
                    title="Specialized"
                    icon={<ToolOutlined />}
                    description="Additional profession profiles"
                    count={specializedProfessionProfiles.length}
                    addDisabled={false}
                    onAdd={() => {
                      const helperId = helperProfile?.id || userId;
                      if (!helperId) return;
                      navigate(
                        `${paths.helperProfessionProfileCreateForHelper(helperId)}?type=SPECIALIZED`,
                      );
                    }}
                  >
                    {renderProfessionCardList(
                      specializedProfessionProfiles,
                      "No specialized profiles.",
                    )}
                  </ProfessionBucketCard>
                </Col>
              </Row>
            </Card>
            </div>
          </Space>
        </Col>

        <Col xs={24} lg={8}>
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <Card title="Helper Details">
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                {helperDetails.map((item) => (
                  <div key={item.label}>
                    <Typography.Text type="secondary" className="text-xxs d-block">
                      {item.label}
                    </Typography.Text>
                    {typeof item.value === "string" ||
                    typeof item.value === "number" ? (
                      <Typography.Text strong>{item.value}</Typography.Text>
                    ) : (
                      item.value
                    )}
                  </div>
                ))}
              </Space>
            </Card>
          </Space>
        </Col>
      </Row>

      <Guard privilege={PRIV.UPDATE_USERS}>
        <BsModal
          show={statusModalOpen}
          onHide={() => setStatusModalOpen(false)}
          centered
          backdrop="static"
        >
          <BsModal.Header closeButton>
            <BsModal.Title className="text-sm">Update Helper Status</BsModal.Title>
          </BsModal.Header>
          <BsModal.Body>
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              <Typography.Text type="secondary">
                Update helper status for {helperDisplayName}.
              </Typography.Text>
              <Form.Select
                size="sm"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control
                as="textarea"
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional status notes"
                maxLength={2000}
              />
            </Space>
          </BsModal.Body>
          <BsModal.Footer className="border-0">
            <Button onClick={() => setStatusModalOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="primary"
              loading={saving}
              onClick={handleSaveStatus}
              disabled={(!statusChanged && !notesChanged) || saving}
            >
              Save Changes
            </Button>
          </BsModal.Footer>
        </BsModal>
      </Guard>

      <Drawer
        open={professionDrawerOpen}
        onClose={closeProfessionDrawer}
        width={professionDrawerFullWidth ? "100vw" : 760}
        zIndex={100060}
        destroyOnClose
        title={
          professionDrawerMode === "create"
            ? "Add Profession Profile"
            : professionDrawerMode === "edit"
              ? "Edit Profession Profile"
              : "Profession Profile Details"
        }
        extra={
          <Space>
            <Button
              type="text"
              aria-label={professionDrawerFullWidth ? "Normal drawer width" : "Expand drawer"}
              title={professionDrawerFullWidth ? "Normal width" : "Expand"}
              icon={professionDrawerFullWidth ? <CompressOutlined /> : <ExpandOutlined />}
              onClick={() => setProfessionDrawerFullWidth((v) => !v)}
            />
            {professionDrawerMode === "view" ? (
              <Button
                icon={<EditOutlined />}
                onClick={() => setProfessionDrawerMode("edit")}
                disabled={professionDrawerLoading}
              >
                Edit
              </Button>
            ) : null}
            <Button onClick={closeProfessionDrawer}>Close</Button>
          </Space>
        }
        styles={{
          wrapper: { maxWidth: "100vw" },
          body: {
            padding: 16,
            paddingLeft: professionDrawerFullWidth ? 48 : 24,
            paddingRight: 24,
          },
          header: {
            paddingLeft: professionDrawerFullWidth ? 48 : 24,
            paddingRight: 24,
          },
        }}
        footer={
          professionDrawerMode === "view" ? null : (
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button onClick={closeProfessionDrawer} disabled={professionSaving}>
                Cancel
              </Button>
              <Button
                type="primary"
                loading={professionSaving}
                disabled={professionSaving}
                onClick={() => professionForm.submit()}
              >
                Save
              </Button>
            </Space>
          )
        }
      >
        <Spin spinning={professionDrawerLoading}>
          <AntForm
            layout="vertical"
            form={professionForm}
            onFinish={saveProfessionProfile}
            disabled={professionDrawerMode === "view"}
          >
            <AntForm.Item name="id" hidden>
              <Input />
            </AntForm.Item>

            {professionDrawerMode === "view" && professionDrawerRecord ? (
              <>
                <Row gutter={[12, 12]}>
                  <Col xs={24} lg={12}>
                    <Card size="small" title="Profile" style={{ borderRadius: 10 }}>
                      <Descriptions bordered size="small" column={1} labelStyle={{ width: 180 }}>
                        <Descriptions.Item label="Profile Type">
                          <Tag>{String(professionDrawerRecord?.profileType || "-").toUpperCase()}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Sector">
                          {professionDrawerRecord?.sector?.label ||
                            professionDrawerRecord?.sector?.code ||
                            "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Profile Name">
                          {professionDrawerRecord?.profileName || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Education Level">
                          {professionDrawerRecord?.educationLevel || "-"}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  </Col>

                  <Col xs={24} lg={12}>
                    <Card size="small" title="Summary" style={{ borderRadius: 10 }}>
                      <Typography.Text type="secondary" style={{ display: "block" }}>
                        {professionDrawerRecord?.profileSummary || "-"}
                      </Typography.Text>
                      <Divider style={{ marginBlock: 10 }} />
                      <Typography.Text type="secondary" style={{ display: "block" }}>
                        {professionDrawerRecord?.description || "-"}
                      </Typography.Text>
                    </Card>
                  </Col>
                </Row>

                <Divider />

                <Card size="small" title="Experience" style={{ borderRadius: 10 }}>
                  <Typography.Text type="secondary" style={{ display: "block" }}>
                    {professionDrawerRecord?.experience || "-"}
                  </Typography.Text>
                </Card>

                <Divider />

                <Row gutter={[12, 12]}>
                  <Col xs={24} md={12}>
                    <Card size="small" title="Documents" style={{ borderRadius: 10 }}>
                      {(Array.isArray(professionDrawerRecord?.documents)
                        ? professionDrawerRecord.documents
                        : []
                      ).length ? (
                        <Space wrap size={10}>
                          {professionDrawerRecord.documents.map((fid, index) => (
                            <div key={`${String(fid)}-${index}`}>
                              {renderAttachmentValue(fid, "Document")}
                            </div>
                          ))}
                        </Space>
                      ) : (
                        <Typography.Text type="secondary">No documents</Typography.Text>
                      )}
                    </Card>
                  </Col>

                  <Col xs={24} md={12}>
                    <Card size="small" title="Certifications" style={{ borderRadius: 10 }}>
                      {(Array.isArray(professionDrawerRecord?.certifications)
                        ? professionDrawerRecord.certifications
                        : []
                      ).length ? (
                        <Space wrap size={10}>
                          {professionDrawerRecord.certifications.map((cert, index) => {
                            const fileId = cert?.fileId || cert?.file?.id || null;
                            if (!fileId) return null;
                            return (
                              <div key={`${String(fileId)}-${index}`}>
                                {renderAttachmentValue(fileId, "Certification")}
                              </div>
                            );
                          })}
                        </Space>
                      ) : (
                        <Typography.Text type="secondary">No certifications</Typography.Text>
                      )}
                    </Card>
                  </Col>
                </Row>
              </>
            ) : (
              <>
                <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                  <div
                    style={{
                      width: 220,
                      position: "sticky",
                      top: 8,
                      alignSelf: "flex-start",
                    }}
                  >
                    <Card size="small" style={{ borderRadius: 10 }}>
                      <Space direction="vertical" size={8} style={{ width: "100%" }}>
                        {[
                          { key: "profile", label: "1) Profile" },
                          { key: "experience", label: "2) Experience" },
                          { key: "references", label: "3) References" },
                          { key: "work", label: "4) Work History" },
                          { key: "education", label: "5) Education" },
                          { key: "documents", label: "6) Documents" },
                          { key: "certifications", label: "7) Certifications" },
                          { key: "interests", label: "8) Interests" },
                        ].map((item) => (
                          <Button
                            key={item.key}
                            type="text"
                            style={{ textAlign: "left", paddingInline: 8 }}
                            onClick={() => {
                              const container = professionEditScrollRef.current;
                              const el = document.getElementById(
                                `profession-section-${item.key}`,
                              );
                              if (!container || !el) return;
                              // Smooth scroll within the right-side container.
                              const top = el.offsetTop - 8;
                              container.scrollTo({ top, behavior: "smooth" });
                            }}
                          >
                            {item.label}
                          </Button>
                        ))}
                      </Space>
                    </Card>
                  </div>

                  <div
                    ref={professionEditScrollRef}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      maxHeight: "calc(100vh - 220px)",
                      overflowY: "auto",
                      paddingRight: 4,
                    }}
                  >
                    <Space direction="vertical" size={14} style={{ width: "100%" }}>
                      <div id="profession-section-profile" />
                      <Card size="small" title="1) Profile" style={{ borderRadius: 10 }}>
                    <Space direction="vertical" size={12} style={{ width: "100%" }}>
                      <Row gutter={[12, 12]}>
                        <Col xs={24} md={12}>
                          <AntForm.Item
                            name="profileType"
                            label="Profile Type"
                            rules={[{ required: true, message: "Profile type is required" }]}
                          >
                            <Select
                              disabled={
                                professionDrawerMode === "create" &&
                                Boolean(professionDrawerLockedProfileType)
                              }
                              options={[
                                { label: "General", value: "GENERAL" },
                                { label: "Specialized", value: "SPECIALIZED" },
                              ]}
                            />
                          </AntForm.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <AntForm.Item
                            name="sectorCode"
                            label="Sector Code"
                            rules={[{ required: true, message: "Sector code is required" }]}
                          >
                            <Input placeholder="e.g. CARE_GIVER" />
                          </AntForm.Item>
                        </Col>
                      </Row>

                      <AntForm.Item name="profileName" label="Profile Name">
                        <Input placeholder="e.g. Caregiver Profile" />
                      </AntForm.Item>

                      <AntForm.Item name="profileSummary" label="Profile Summary">
                        <Input.TextArea rows={2} placeholder="Short summary..." />
                      </AntForm.Item>

                      <AntForm.Item name="description" label="Description">
                        <Input.TextArea rows={3} placeholder="Description..." />
                      </AntForm.Item>
                    </Space>
                  </Card>

                  <div id="profession-section-experience" />
                  <Card size="small" title="2) Experience" style={{ borderRadius: 10 }}>
                    <Space direction="vertical" size={12} style={{ width: "100%" }}>
                      <AntForm.Item name="experience" label="Experience">
                        <Input.TextArea rows={3} placeholder="Experience details..." />
                      </AntForm.Item>
                      <AntForm.Item name="previousRegionsWorkedIn" label="Previous Regions Worked In">
                        <GooglePlacesTagsSelect
                          googleMapsApiKey={googleMapsApiKey}
                          placeholder="Add regions..."
                          types={["(cities)"]}
                          resolveValueFromDetails={regionSummaryFromPlaceDetails}
                        />
                      </AntForm.Item>
                    </Space>
                  </Card>

                  <div id="profession-section-references" />
                  <Card size="small" title="3) References" style={{ borderRadius: 10 }}>
                    <AntForm.List name="references">
                      {(fields, { add, remove }) => (
                        <Space direction="vertical" size={12} style={{ width: "100%" }}>
                          {fields.map((field) => (
                            <Card
                              key={field.key}
                              size="small"
                              style={{ borderRadius: 10 }}
                              styles={{ body: { padding: 12 } }}
                              title={
                                professionForm.getFieldValue(["references", field.name, "name"]) ||
                                `Reference ${field.name + 1}`
                              }
                              extra={
                                <Button danger type="text" onClick={() => remove(field.name)}>
                                  Remove
                                </Button>
                              }
                            >
                              <Row gutter={[12, 12]}>
                                <Col xs={24} md={12}>
                                  <AntForm.Item {...field} name={[field.name, "name"]} label="Name">
                                    <Input />
                                  </AntForm.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                  <AntForm.Item {...field} name={[field.name, "relationship"]} label="Relationship">
                                    <Input />
                                  </AntForm.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                  <AntForm.Item {...field} name={[field.name, "phone"]} label="Phone">
                                    <Input />
                                  </AntForm.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                  <AntForm.Item {...field} name={[field.name, "email"]} label="Email">
                                    <Input />
                                  </AntForm.Item>
                                </Col>
                                <Col xs={24}>
                                  <AntForm.Item {...field} name={[field.name, "notes"]} label="Notes">
                                    <Input.TextArea rows={2} />
                                  </AntForm.Item>
                                </Col>
                              </Row>
                            </Card>
                          ))}
                          <Button type="dashed" onClick={() => add({})} icon={<PlusOutlined />}>
                            Add reference
                          </Button>
                        </Space>
                      )}
                    </AntForm.List>
                  </Card>

                  <div id="profession-section-work" />
                  <Card size="small" title="4) Work Experience History" style={{ borderRadius: 10 }}>
                    <AntForm.List name="workExperienceHistory">
                      {(fields, { add, remove }) => (
                        <Space direction="vertical" size={12} style={{ width: "100%" }}>
                          {fields.map((field) => (
                            <Card
                              key={field.key}
                              size="small"
                              style={{ borderRadius: 10 }}
                              styles={{ body: { padding: 12 } }}
                              title={
                                professionForm.getFieldValue(["workExperienceHistory", field.name, "organization"]) ||
                                `Work experience ${field.name + 1}`
                              }
                              extra={
                                <Button danger type="text" onClick={() => remove(field.name)}>
                                  Remove
                                </Button>
                              }
                            >
                              <Row gutter={[12, 12]}>
                                <Col xs={24} md={12}>
                                  <AntForm.Item {...field} name={[field.name, "organization"]} label="Organization">
                                    <Input />
                                  </AntForm.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                  <AntForm.Item {...field} name={[field.name, "role"]} label="Role">
                                    <Input />
                                  </AntForm.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                  <AntForm.Item {...field} name={[field.name, "location"]} label="Location">
                                    <GooglePlacesAutocompleteInput
                                      googleMapsApiKey={googleMapsApiKey}
                                      placeholder="Search work location"
                                      types={["geocode"]}
                                      onResolvePlace={async ({ details, prediction }) => {
                                        const resolved =
                                          details?.formatted_address ||
                                          prediction?.description ||
                                          "";
                                        professionForm.setFieldValue(
                                          ["workExperienceHistory", field.name, "location"],
                                          resolved,
                                        );
                                      }}
                                    />
                                  </AntForm.Item>
                                </Col>
                                <Col xs={24} md={6}>
                                  <AntForm.Item
                                    {...field}
                                    name={[field.name, "startDate"]}
                                    label="Start Date"
                                    getValueProps={(value) => ({
                                      value: value ? dayjs(String(value)) : null,
                                    })}
                                    getValueFromEvent={(value) => (value ? value.format("YYYY-MM-DD") : "")}
                                  >
                                    <DatePicker style={{ width: "100%" }} />
                                  </AntForm.Item>
                                </Col>
                                <Col xs={24} md={6}>
                                  <AntForm.Item
                                    {...field}
                                    name={[field.name, "endDate"]}
                                    label="End Date"
                                    getValueProps={(value) => ({
                                      value: value ? dayjs(String(value)) : null,
                                    })}
                                    getValueFromEvent={(value) => (value ? value.format("YYYY-MM-DD") : "")}
                                  >
                                    <DatePicker style={{ width: "100%" }} />
                                  </AntForm.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                  <AntForm.Item {...field} name={[field.name, "current"]} valuePropName="checked">
                                    <Checkbox>Current</Checkbox>
                                  </AntForm.Item>
                                </Col>
                                <Col xs={24}>
                                  <AntForm.Item {...field} name={[field.name, "notes"]} label="Notes">
                                    <Input.TextArea rows={2} />
                                  </AntForm.Item>
                                </Col>
                              </Row>
                            </Card>
                          ))}
                          <Button type="dashed" onClick={() => add({})} icon={<PlusOutlined />}>
                            Add work experience
                          </Button>
                        </Space>
                      )}
                    </AntForm.List>
                  </Card>

                  <div id="profession-section-education" />
                  <Card size="small" title="5) Education" style={{ borderRadius: 10 }}>
                    <Space direction="vertical" size={12} style={{ width: "100%" }}>
                      <Row gutter={[12, 12]}>
                        <Col xs={24} md={12}>
                          <AntForm.Item name="educationLevel" label="Education Level">
                            <Select
                              allowClear
                              options={[
                                { label: "None", value: "NONE" },
                                { label: "High School", value: "HIGH_SCHOOL" },
                                { label: "GED", value: "GED" },
                                { label: "Certificate", value: "CERTIFICATE" },
                                { label: "Associate", value: "ASSOCIATE" },
                                { label: "Bachelor", value: "BACHELOR" },
                                { label: "Master", value: "MASTER" },
                                { label: "Doctorate", value: "DOCTORATE" },
                                { label: "Other", value: "OTHER" },
                              ]}
                            />
                          </AntForm.Item>
                        </Col>
                      </Row>

                      <AntForm.List name="education">
                        {(fields, { add, remove }) => (
                          <Space direction="vertical" size={12} style={{ width: "100%" }}>
                            {fields.map((field) => (
                              <Card
                                key={field.key}
                                size="small"
                                style={{ borderRadius: 10 }}
                                styles={{ body: { padding: 12 } }}
                                title={
                                  professionForm.getFieldValue(["education", field.name, "institution"]) ||
                                  `Education ${field.name + 1}`
                                }
                                extra={
                                  <Button danger type="text" onClick={() => remove(field.name)}>
                                    Remove
                                  </Button>
                                }
                              >
                                <Row gutter={[12, 12]}>
                                  <Col xs={24} md={12}>
                                    <AntForm.Item {...field} name={[field.name, "institution"]} label="Institution">
                                      <Input />
                                    </AntForm.Item>
                                  </Col>
                                  <Col xs={24} md={12}>
                                    <AntForm.Item {...field} name={[field.name, "program"]} label="Program">
                                      <Input />
                                    </AntForm.Item>
                                  </Col>
                                <Col xs={24} md={12}>
                                  <AntForm.Item {...field} name={[field.name, "educationLevel"]} label="Education Level">
                                    <Select
                                      allowClear
                                      placeholder="Select level"
                                      options={[
                                        { value: "HIGH_SCHOOL", label: "High School" },
                                        { value: "VOCATIONAL_CERTIFICATE", label: "Vocational Certificate" },
                                        { value: "DIPLOMA_ADVANCED_DIPLOMA", label: "Diploma / Advanced Diploma" },
                                        { value: "ASSOCIATE_DEGREE", label: "Associate Degree" },
                                        { value: "BACHELOR_DEGREE", label: "Bachelor's Degree" },
                                        { value: "POSTGRADUATE_CERTIFICATE", label: "Postgraduate Certificate" },
                                        { value: "MASTER_DEGREE", label: "Master's Degree" },
                                        { value: "DOCTORATE", label: "Doctorate" },
                                        { value: "PROFESSIONAL_CERTIFICATION", label: "Professional Certification" },
                                        { value: "OTHER", label: "Other" },
                                      ]}
                                    />
                                  </AntForm.Item>
                                </Col>
                                  <Col xs={24} md={12}>
                                    <AntForm.Item
                                      {...field}
                                      name={[field.name, "graduationYear"]}
                                      label="Graduation Year"
                                      getValueProps={(value) => ({
                                        value: value ? dayjs(String(value), "YYYY") : null,
                                      })}
                                      getValueFromEvent={(value) => (value ? value.format("YYYY") : "")}
                                    >
                                      <DatePicker picker="year" format="YYYY" style={{ width: "100%" }} />
                                    </AntForm.Item>
                                  </Col>
                                  <Col xs={24}>
                                    <AntForm.Item {...field} name={[field.name, "notes"]} label="Notes">
                                      <Input.TextArea rows={2} />
                                    </AntForm.Item>
                                  </Col>
                                </Row>
                              </Card>
                            ))}
                            <Button type="dashed" onClick={() => add({})} icon={<PlusOutlined />}>
                              Add education
                            </Button>
                          </Space>
                        )}
                      </AntForm.List>
                    </Space>
                  </Card>

                  <div id="profession-section-documents" />
                  <Card size="small" title="6) Documents" style={{ borderRadius: 10 }}>
                    <AntForm.Item label="Documents" name="documents">
                      <Upload
                        multiple
                        listType="picture-card"
                        fileList={professionDocumentsFiles.map((f) => {
                          const fid = extractFileId(f?.fileId || f?.uid);
                          const signed = fid ? signedUrlByFileId?.[String(fid)] : null;
                          return {
                            ...f,
                            url: signed || f.url,
                            thumbUrl: signed || f.thumbUrl,
                          };
                        })}
                        customRequest={async ({ file, onSuccess, onError }) => {
                          try {
                            setProfessionDocumentsUploading(true);
                            const uploaderId = ensureUploaderId();
                            if (!uploaderId) throw new Error("Missing uploader id");
                            const res = await uploadFile(uploaderId, file, {
                              purpose: "USER_PROFILE",
                              fileType: "DOCUMENT",
                            });
                            const fid = res?.fileId;
                            if (!fid) throw new Error("Upload failed: missing fileId");

                            const next = [
                              ...(Array.isArray(professionDocumentsFiles) ? professionDocumentsFiles : []),
                              { uid: String(fid), fileId: String(fid), name: file?.name || "document", status: "done" },
                            ];
                            setProfessionDocumentsFiles(next);
                            syncDocumentsFormValueFromFileList(next);
                            await fetchSignedUrlForFileId(fid);
                            onSuccess?.(res);
                            message.success(`${file?.name || "Document"} uploaded`);
                          } catch (err) {
                            console.error("[helper-professions] document upload failed", err);
                            message.error(err?.message || "Failed to upload document");
                            onError?.(err);
                          } finally {
                            setProfessionDocumentsUploading(false);
                          }
                        }}
                        onRemove={(file) => {
                          const uid = String(file?.uid || "");
                          const next = (Array.isArray(professionDocumentsFiles) ? professionDocumentsFiles : []).filter(
                            (f) => String(f?.uid) !== uid,
                          );
                          setProfessionDocumentsFiles(next);
                          syncDocumentsFormValueFromFileList(next);
                          return true;
                        }}
                        onPreview={(file) => {
                          const fid = extractFileId(file?.fileId || file?.uid);
                          openAttachmentPreview(fid, file?.name || "Document");
                        }}
                      >
                        <div style={{ opacity: professionDocumentsUploading ? 0.6 : 1 }}>
                          <PlusOutlined />
                          <div style={{ marginTop: 8, fontSize: 12 }}>Upload</div>
                        </div>
                      </Upload>
                    </AntForm.Item>
                  </Card>

                  <div id="profession-section-certifications" />
                  <Card size="small" title="7) Certifications" style={{ borderRadius: 10 }}>
                    <AntForm.List name="certifications">
                      {(fields, { add, remove }) => (
                        <Space direction="vertical" size={12} style={{ width: "100%" }}>
                          {fields.map((field) => {
                            const current = professionForm.getFieldValue(["certifications", field.name]);
                            const fileId = extractFileId(current?.fileId);
                            return (
                              <Card
                                key={field.key}
                                size="small"
                                style={{ borderRadius: 10 }}
                                styles={{ body: { padding: 12 } }}
                                title={current?.name || `Certification ${field.name + 1}`}
                                extra={
                                  <Button danger type="text" onClick={() => remove(field.name)}>
                                    Remove
                                  </Button>
                                }
                              >
                                <Row gutter={[12, 12]}>
                                  <Col xs={24} md={12}>
                                    <AntForm.Item {...field} name={[field.name, "name"]} label="Name">
                                      <Input placeholder="e.g. Certificate of Completion" />
                                    </AntForm.Item>
                                  </Col>
                                  <Col xs={24} md={12}>
                                    <AntForm.Item {...field} name={[field.name, "issuer"]} label="Issuer">
                                      <Input placeholder="e.g. Red Cross" />
                                    </AntForm.Item>
                                  </Col>
                                  <Col xs={24} md={12}>
                                    <AntForm.Item {...field} name={[field.name, "certificationNumber"]} label="Certification Number">
                                      <Input placeholder="Optional" />
                                    </AntForm.Item>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <AntForm.Item
                                      {...field}
                                      name={[field.name, "issueDate"]}
                                      label="Issue Date"
                                      getValueProps={(value) => ({
                                        value: value ? dayjs(String(value)) : null,
                                      })}
                                      getValueFromEvent={(value) => (value ? value.format("YYYY-MM-DD") : "")}
                                    >
                                      <DatePicker style={{ width: "100%" }} />
                                    </AntForm.Item>
                                  </Col>
                                  <Col xs={24} md={6}>
                                    <AntForm.Item
                                      {...field}
                                      name={[field.name, "expiryDate"]}
                                      label="Expiry Date"
                                      getValueProps={(value) => ({
                                        value: value ? dayjs(String(value)) : null,
                                      })}
                                      getValueFromEvent={(value) => (value ? value.format("YYYY-MM-DD") : "")}
                                    >
                                      <DatePicker style={{ width: "100%" }} />
                                    </AntForm.Item>
                                  </Col>
                                  <Col xs={24}>
                                    <AntForm.Item {...field} name={[field.name, "fileId"]} label="Certificate File">
                                      <Space wrap>
                                        {fileId ? (
                                          renderAttachmentValue(fileId, current?.name || "Certification")
                                        ) : (
                                          <Typography.Text type="secondary">No file uploaded</Typography.Text>
                                        )}
                                        <Upload
                                          showUploadList={false}
                                          customRequest={async ({ file, onSuccess, onError }) => {
                                            try {
                                              const uploaderId = ensureUploaderId();
                                              if (!uploaderId) throw new Error("Missing uploader id");
                                              const res = await uploadFile(uploaderId, file, {
                                                purpose: "USER_PROFILE",
                                                fileType: "DOCUMENT",
                                              });
                                              const fid = res?.fileId;
                                              if (!fid) throw new Error("Upload failed: missing fileId");
                                              professionForm.setFieldValue(["certifications", field.name, "fileId"], String(fid));
                                              await fetchSignedUrlForFileId(fid);
                                              onSuccess?.(res);
                                              message.success(`${file?.name || "File"} uploaded`);
                                            } catch (err) {
                                              console.error("[helper-professions] certification upload failed", err);
                                              message.error(err?.message || "Failed to upload");
                                              onError?.(err);
                                            }
                                          }}
                                        >
                                          <Button size="small" icon={<PlusOutlined />}>Upload file</Button>
                                        </Upload>
                                      </Space>
                                    </AntForm.Item>
                                  </Col>
                                </Row>
                              </Card>
                            );
                          })}
                          <Button type="dashed" onClick={() => add({})} icon={<PlusOutlined />}>
                            Add certification
                          </Button>
                        </Space>
                      )}
                    </AntForm.List>
                  </Card>

                  <div id="profession-section-interests" />
                  <Card size="small" title="8) Interests" style={{ borderRadius: 10 }}>
                    <Row gutter={[12, 12]}>
                      <Col xs={24} md={12}>
                        <AntForm.Item
                          name="interestedServices"
                          label="Interested Services (codes)"
                        >
                          <Select
                            mode="multiple"
                            allowClear
                            loading={professionServiceOfferingsLoading}
                            options={professionServiceOfferingOptions}
                            showSearch
                            placeholder="Select service codes"
                            filterOption={(input, option) =>
                              String(option?.label || "")
                                .toLowerCase()
                                .includes(String(input || "").toLowerCase())
                            }
                          />
                        </AntForm.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <AntForm.Item name="interestedShifts" label="Shift Availability">
                          <Select
                            mode="multiple"
                            allowClear
                            options={[
                              { label: "Single", value: "SINGLE" },
                              { label: "Double", value: "DOUBLE" },
                              { label: "Partial", value: "PARTIAL" },
                              { label: "Open", value: "OPEN" },
                            ]}
                          />
                        </AntForm.Item>
                      </Col>
                    </Row>

                    <AntForm.List name="interestedLocations">
                      {(fields, { add, remove }) => (
                        <Space direction="vertical" size={12} style={{ width: "100%" }}>
                          <Space style={{ width: "100%", justifyContent: "space-between" }}>
                            <Typography.Text strong>Interested Locations</Typography.Text>
                            <Button type="dashed" size="small" onClick={() => add({ open: true })} icon={<PlusOutlined />}>
                              Add location
                            </Button>
                          </Space>
                          {fields.map((field) => (
                            <Card
                              key={field.key}
                              size="small"
                              style={{ borderRadius: 10 }}
                              styles={{ body: { padding: 12 } }}
                              title={
                                professionForm.getFieldValue(["interestedLocations", field.name, "city"]) ||
                                `Location ${field.name + 1}`
                              }
                              extra={
                                <Button danger type="text" onClick={() => remove(field.name)}>
                                  Remove
                                </Button>
                              }
                            >
                              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                    Search or enter places like city, county, state, country.
                                  </Typography.Text>
                                  <GooglePlacesAutocompleteInput
                                    googleMapsApiKey={googleMapsApiKey}
                                    placeholder="Search interested location"
                                    onResolvePlace={async ({ details }) => {
                                      const next = locationFieldsFromPlaceDetails(details);
                                      professionForm.setFieldValue(["interestedLocations", field.name, "city"], next.city || "");
                                      professionForm.setFieldValue(["interestedLocations", field.name, "state"], next.state || "");
                                      professionForm.setFieldValue(["interestedLocations", field.name, "county"], next.county || "");
                                      professionForm.setFieldValue(["interestedLocations", field.name, "country"], next.country || "USA");
                                    }}
                                  />
                                </div>
                                <AntForm.Item {...field} name={[field.name, "city"]} hidden>
                                  <Input type="hidden" autoComplete="off" />
                                </AntForm.Item>
                                <AntForm.Item {...field} name={[field.name, "state"]} hidden>
                                  <Input type="hidden" autoComplete="off" />
                                </AntForm.Item>
                                <AntForm.Item {...field} name={[field.name, "county"]} hidden>
                                  <Input type="hidden" autoComplete="off" />
                                </AntForm.Item>
                                <AntForm.Item {...field} name={[field.name, "country"]} hidden>
                                  <Input type="hidden" autoComplete="off" />
                                </AntForm.Item>
                                <AntForm.Item
                                  {...field}
                                  name={[field.name, "open"]}
                                  valuePropName="checked"
                                  style={{ marginBottom: 0 }}
                                >
                                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <Typography.Text>Open to this location</Typography.Text>
                                    <Switch />
                                  </div>
                                </AntForm.Item>
                              </Space>
                            </Card>
                          ))}
                        </Space>
                      )}
                    </AntForm.List>
                  </Card>
                    </Space>
                  </div>
                </div>

              </>
            )}
          </AntForm>
        </Spin>

        <Modal
          open={professionAttachmentPreviewOpen}
          title={professionAttachmentPreviewTitle}
          footer={null}
          onCancel={() => setProfessionAttachmentPreviewOpen(false)}
          zIndex={100500}
          centered
          destroyOnClose
        >
          <div style={{ textAlign: "center" }}>
            <Image
              src={professionAttachmentPreviewUrl}
              alt={professionAttachmentPreviewTitle}
              style={{ maxHeight: "70vh", objectFit: "contain" }}
            />
          </div>
        </Modal>
      </Drawer>
    </Space>
  );
};

export default HelperProfilePage;
